import { getConversationRollbackSnapshot } from 'src/modules/shared/communications/utils/cache/funnel-conversations/rollback-snapshot.util';
import { isConversationOperationConflict } from '../../../utils/conversation-operation-conflict.util';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type FunnelConversation,
  CrmFunnelsConversationsService,
  type FunnelConversationDocument,
  type FunnelConversationSearchExportBodyParams,
  type FunnelConversationSearchExportDownloadResponse,
  type FunnelConversationSearchExportResponse,
} from 'src/api/generated';
import { getModelFromCache } from 'src/modules/shared/utils/cache/update.util';
import { updateFunnelConversationCache } from 'src/modules/shared/communications/utils/cache/funnel-conversations/update.util';
import { createOptimisticConversation } from 'src/modules/shared/communications/utils/cache/funnel-conversations/optimistic.util';
import { captureConversationMutationCache } from 'src/modules/shared/communications/utils/cache/funnel-conversations/mutation-ownership.util';
import { recoverCanonicalConversation } from 'src/modules/shared/communications/services/canonical-conversation-recovery.service';
import type { UpdateFunnelConversationContext } from '../../../../shared/communications/types/cache/funnel-conversations/optimistic.types';
import type { UpdateFunnelConversationParams } from '../../../../shared/communications/types/cache/funnel-conversations/optimistic.types';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useAlert } from 'src/contexts/alert-context';

interface UseUpdateFunnelConversationResponse {
  loading: boolean;
  error: Error | null;
  conversation?: FunnelConversation;
  updateConversation: (
    params: UpdateFunnelConversationParams
  ) => Promise<FunnelConversation>;
}

export const useUpdateFunnelConversation = (
  onSuccess: (
    conversation: FunnelConversation,
    context: UpdateFunnelConversationContext
  ) => void = () => undefined,
  onError: (
    error: Error,
    context: UpdateFunnelConversationContext
  ) => void = () => undefined,
  onOptimisticUpdate: (
    conversation: FunnelConversation,
    context: UpdateFunnelConversationContext
  ) => void = () => undefined
): UseUpdateFunnelConversationResponse => {
  const queryClient = useQueryClient();
  const { showAlert } = useAlert();
  const { t } = useI18n();

  const updateFunnelConversation = async ({
    id,
    data,
  }: UpdateFunnelConversationParams): Promise<FunnelConversation> => {
    return await CrmFunnelsConversationsService.updateFunnelConversation(
      id,
      data
    );
  };

  const mutation = useMutation({
    mutationKey: ['crm/funnel-conversations/update'],
    mutationFn: updateFunnelConversation,
    onMutate: async (params) => {
      await queryClient.cancelQueries({
        queryKey: ['crm/funnel-conversations'],
      });
      await queryClient.cancelQueries({
        queryKey: ['crm/documents/funnel-conversations'],
      });

      const previousConversationDocument =
        getModelFromCache<FunnelConversationDocument>(
          queryClient,
          ['crm/documents/funnel-conversations'],
          params.id
        );
      const previousConversation = getModelFromCache<FunnelConversation>(
        queryClient,
        ['crm/funnel-conversations'],
        params.id
      );

      const optimisticConversation = createOptimisticConversation(
        params,
        previousConversation
      );

      const context: UpdateFunnelConversationContext = {
        params,
        previousConversation,
        previousConversationDocument,
      };

      updateFunnelConversationCache(
        queryClient,
        context,
        optimisticConversation
      );
      onOptimisticUpdate(optimisticConversation, context);

      return {
        ...context,
        getCacheState: captureConversationMutationCache(queryClient, params.id),
      };
    },
    onError: (error, params, context) => {
      if (
        context &&
        (context.previousConversation || context.previousConversationDocument)
      ) {
        updateFunnelConversationCache(
          queryClient,
          context,
          getConversationRollbackSnapshot(context.previousConversation),
          getConversationRollbackSnapshot(context.previousConversationDocument)
        );
      }

      // Lifecycle callbacks must run for every rejection, including retryable conflicts.
      onError(error, context ?? { params });
      if (isConversationOperationConflict(error)) {
        showAlert(
          t('communications.conversation.operationConflict'),
          'warning'
        );
      }
    },
    onSuccess: (updatedConversation, _params, context) => {
      const cacheState = context.getCacheState(updatedConversation.tenantId);
      if (cacheState !== 'current') {
        const pendingSaves = queryClient.isMutating({
          mutationKey: ['crm/funnel-conversations/update'],
          predicate: ({ state }) => {
            const variables: unknown = state.variables;
            return (
              typeof variables === 'object' &&
              variables !== null &&
              'id' in variables &&
              variables.id === updatedConversation.id
            );
          },
        });
        if (cacheState === 'changed' && pendingSaves <= 1) {
          void recoverCanonicalConversation(
            queryClient,
            updatedConversation.id,
            updatedConversation.tenantId,
            updatedConversation.updatedAt
          );
        }
      }
      const previousArchivedAt =
        context.previousConversation?.archivedAt ??
        context.previousConversationDocument?.archivedAt;
      const becameArchived =
        context.params.data.funnelStatusId !== undefined &&
        !previousArchivedAt &&
        Boolean(updatedConversation.archivedAt);
      const cacheContext = {
        ...context,
        params: {
          ...context.params,
          data: {
            ...context.params.data,
            archivedAt: updatedConversation.archivedAt,
          },
        },
      };
      if (cacheState === 'current') {
        updateFunnelConversationCache(
          queryClient,
          cacheContext,
          updatedConversation
        );
      }
      if (becameArchived) {
        showAlert(t('communications.conversation.autoArchived'), 'success');
      }
      onSuccess(updatedConversation, context);
    },
  });

  return {
    loading: mutation.isPending,
    error: mutation.error,
    conversation: mutation.data,
    updateConversation: mutation.mutateAsync,
  };
};

interface UseArchiveFunnelConversationResponse {
  loading: boolean;
  error: Error | null;
  conversation?: FunnelConversation;
  archiveConversation: (id: number) => Promise<FunnelConversation>;
}

export const useArchiveFunnelConversation = (
  archive: boolean,
  onSuccess: (conversation: FunnelConversation) => void = () => undefined,
  onError: (error: Error) => void = () => undefined
): UseArchiveFunnelConversationResponse => {
  const { updateConversation, loading, error, conversation } =
    useUpdateFunnelConversation(
      (updatedConversation) => {
        onSuccess(updatedConversation);
      },
      (error) => {
        onError(error);
      }
    );

  const archiveConversation = async (
    id: number
  ): Promise<FunnelConversation> => {
    return await updateConversation({
      id,
      data: { archivedAt: archive ? new Date().toISOString() : null },
    });
  };

  return {
    loading,
    error,
    conversation,
    archiveConversation,
  };
};

interface UseExportFunnelConversationTableResponse {
  loading: boolean;
  error: Error | null;
  response?: FunnelConversationSearchExportResponse;
  exportConversationTable: (
    params: FunnelConversationSearchExportBodyParams
  ) => Promise<FunnelConversationSearchExportResponse>;
}

export const useExportFunnelConversationTable = (
  onSuccess: (response: FunnelConversationSearchExportResponse) => void = () =>
    undefined,
  onError: (error: Error) => void = () => undefined
): UseExportFunnelConversationTableResponse => {
  const exportConversationTable = async (
    params: FunnelConversationSearchExportBodyParams
  ): Promise<FunnelConversationSearchExportResponse> => {
    return await CrmFunnelsConversationsService.exportFunnelConversations(
      params
    );
  };

  const mutation = useMutation({
    mutationFn: exportConversationTable,
    onSuccess: (response) => {
      onSuccess(response);
    },
    onError: (error) => {
      onError(error);
    },
  });

  return {
    loading: mutation.isPending,
    error: mutation.error,
    response: mutation.data,
    exportConversationTable: mutation.mutateAsync,
  };
};

interface UseDownloadFunnelConversationTableExportParams {
  fileId: string;
  requestId: string;
}

interface UseDownloadFunnelConversationTableExportResponse {
  loading: boolean;
  error: Error | null;
  response?: FunnelConversationSearchExportDownloadResponse;
  downloadConversationTableExport: (
    params: UseDownloadFunnelConversationTableExportParams
  ) => Promise<FunnelConversationSearchExportDownloadResponse>;
}

export const useDownloadFunnelConversationTableExport = (
  onSuccess: (
    response: FunnelConversationSearchExportDownloadResponse
  ) => void = () => undefined,
  onError: (error: Error) => void = () => undefined
): UseDownloadFunnelConversationTableExportResponse => {
  const downloadConversationTableExport = async ({
    fileId,
    requestId,
  }: UseDownloadFunnelConversationTableExportParams): Promise<FunnelConversationSearchExportDownloadResponse> => {
    return await CrmFunnelsConversationsService.getFunnelConversationExportDownloadUrl(
      fileId,
      requestId
    );
  };

  const mutation = useMutation({
    mutationFn: downloadConversationTableExport,
    onSuccess: (response) => {
      onSuccess(response);
    },
    onError: (error) => {
      onError(error);
    },
  });

  return {
    loading: mutation.isPending,
    error: mutation.error,
    response: mutation.data,
    downloadConversationTableExport: mutation.mutateAsync,
  };
};
